import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {testIdProps} from '@github-ui/test-id-props'
import {useUser} from '@github-ui/use-user'
import {StackIcon, TrashIcon} from '@primer/octicons-react'
import {Button, IconButton, PageLayout, Spinner, useResponsiveValue} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useCallback, useEffect, useRef, useState} from 'react'
import {GiveFeedback} from '../../../components/GiveFeedback'
import type {
  IndexModelsPayload,
  MessagePair,
  ModelInputChangeParams,
  ModelState,
  ShowModelPayload,
} from '../../../types'
import type {AzureModelClient} from '../../../utils/azure-model-client'
import {validateAndFilterParameters} from '../../../utils/model-state'
import {Panel} from '../../../utils/playground-manager'
import {getPromptLocalStorage} from '../../../utils/prompt-local-storage'
import {PlaygroundChatMessage} from '../../playground/components/PlaygroundChatMessage'
import {PlaygroundChatMessageHeader} from '../../playground/components/PlaygroundChatMessageHeader'
import {usePromptState} from '../contexts/PromptStateContext'
import {useExtractedPrompt} from '../hooks/use-extracted-prompt'
import {usePromptManager} from '../prompt-manager'
import {referencedVariablesInState, replaceVars} from '../variables'
import {Header} from './Header'
import ParameterSettingsMenu from './ParameterSettingsMenu'
import styles from './Prompt.module.css'
import {PromptAutocompleteInput} from './PromptAutocompleteInput'
import {PromptFeedbackBanner} from './PromptFeedbackBanner'
import {PromptMessagePair} from './PromptMessagePair'
import {VariablesDialog} from './VariablesDialog'
import type {Model} from '@github-ui/marketplace-common'
import type {RepoModel} from '@github-ui/github-models-repo'

export type PromptProps = {
  modelClient: AzureModelClient
  availableModels: Model[] | RepoModel[]
  isLoadingModels: boolean
}

export function Prompt({modelClient, availableModels, isLoadingModels}: PromptProps) {
  const {prompt, systemPrompt, variables, model, promptFeedbackBannerDismissed, messagePairs} = usePromptState()
  const {gettingStarted, modelInputSchema, catalogData, messages, isLoading} = model
  const manager = usePromptManager()

  const {promptExtractionModel} = useRoutePayload<ShowModelPayload>()

  const [showRunVariablesDialog, setShowRunVariablesDialog] = useState(false)
  const [requiredVariables, setRequiredVariables] = useState<Set<string> | undefined>(undefined)

  const [variableKeys, setVariableKeys] = useState<string[]>(() => {
    if (!prompt) return []
    return referencedVariablesInState({prompt, systemPrompt})
  })

  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = useCallback(() => {
    messagesContainerRef.current?.scrollIntoView(false)
  }, [])

  useEffect(() => {
    requestAnimationFrame(scrollToBottom)
  }, [messages, scrollToBottom])

  const handleRun = useCallback(
    (vars: Record<string, string>) => {
      const variablesWithoutValue = variableKeys.filter(v => !vars[v])
      if (variablesWithoutValue.length > 0) {
        // One of the referenced variables doesn't have a value, ask the user for a value first
        setRequiredVariables(new Set(variablesWithoutValue))
        setShowRunVariablesDialog(true)
        return
      }

      const expandedPrompt = replaceVars(prompt, vars)
      const expandedSystemPrompt = systemPrompt ? replaceVars(systemPrompt, vars) : systemPrompt

      if (messagePairs) {
        const expandedMessagePairs = messagePairs.map(pair => ({
          assistant: replaceVars(pair.assistant, vars),
          user: replaceVars(pair.user, vars),
        }))
        manager.sendMessage(model, modelClient, expandedSystemPrompt, expandedPrompt, [], expandedMessagePairs)
      } else {
        manager.sendMessage(model, modelClient, expandedSystemPrompt, expandedPrompt)
      }
    },
    [variableKeys, prompt, systemPrompt, messagePairs, manager, model, modelClient],
  )

  const handleStop = useCallback(() => {
    modelClient.stopStreamingMessages(Panel.Main)
  }, [modelClient])

  const {promptExtractionCodeSnippet} = useRoutePayload<IndexModelsPayload>()

  const [showVariablesDialog, setShowVariablesDialog] = useState(false)
  const handleCloseEditVariablesDialog = useCallback(
    (v?: Record<string, string>) => {
      if (v) {
        manager.setVariables(v)
      }
      setShowVariablesDialog(false)
    },
    [manager],
  )

  const handleCloseRunVariablesDialog = useCallback(
    (v?: Record<string, string>) => {
      setShowRunVariablesDialog(false)

      if (v) {
        // Persist variables
        manager.setVariables(v)

        // Reset required variables
        setRequiredVariables(undefined)

        handleRun(v)
      }
    },
    [manager, handleRun],
  )

  return (
    <div className={styles.promptContainer}>
      {showVariablesDialog && (
        <VariablesDialog
          primaryTitle="Save"
          variables={variables}
          availableVariables={variableKeys}
          onClose={handleCloseEditVariablesDialog}
        />
      )}

      {showRunVariablesDialog && (
        <VariablesDialog
          primaryTitle="Run"
          variables={variables}
          availableVariables={variableKeys}
          requiredVariables={requiredVariables}
          onClose={handleCloseRunVariablesDialog}
        />
      )}

      <div className={styles.mobileHeader}>
        <GiveFeedback mobile />
      </div>
      <div className={styles.promptWrapper}>
        <div className={styles.responsiveWrapper}>
          <div className={styles.responsiveContainer}>
            <Header
              run={() => handleRun(variables)}
              stop={handleStop}
              running={isLoading}
              canRun={!!prompt}
              model={catalogData}
              modelInputSchema={modelInputSchema}
              gettingStarted={gettingStarted}
              availableModels={availableModels}
              isLoadingModels={isLoadingModels}
            />
            <div className="border overflow-hidden rounded-2 flex-grow-1 height-full">
              <PromptToolbar
                modelClient={modelClient}
                setShowVariablesDialog={setShowVariablesDialog}
                variableKeys={variableKeys}
              />
              <PageLayout padding="none" containerWidth="full" columnGap="none" className={styles.promptLayout}>
                <PageLayout.Pane
                  aria-label="System and user prompts"
                  resizable
                  position={{narrow: 'end', regular: 'start', wide: 'start'}}
                  divider="none"
                  padding="none"
                >
                  <PromptPaneContents setVariableKeys={setVariableKeys} variableKeys={variableKeys} />
                </PageLayout.Pane>
                <PageLayout.Content as="div" className={styles.promptBody}>
                  <div className="height-full d-flex flex-column">
                    {!promptFeedbackBannerDismissed && (
                      <PromptFeedbackBanner onDismiss={() => manager.dismissPromptFeedbackBanner()} />
                    )}

                    {messages?.length > 0 ? (
                      <PromptMessagesContainer model={model} ref={messagesContainerRef} />
                    ) : (
                      <PromptBlankslate
                        modelClient={modelClient}
                        promptExtractionCodeSnippet={promptExtractionCodeSnippet}
                        promptExtractionModel={promptExtractionModel}
                      />
                    )}
                  </div>
                </PageLayout.Content>
              </PageLayout>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PromptToolbar({
  modelClient,
  setShowVariablesDialog,
  variableKeys,
}: Pick<PromptProps, 'modelClient'> & {setShowVariablesDialog: (show: boolean) => void; variableKeys: string[]}) {
  const {model, prompt, systemPrompt} = usePromptState()
  const {modelInputSchema} = model
  const isMobile = useResponsiveValue({narrow: true}, false)
  const manager = usePromptManager()
  const localStorage = getPromptLocalStorage()
  const modelStateWithParameters = localStorage?.parameters ? {...model, parameters: localStorage.parameters} : model

  const handleModelParamsChange = ({key, value, validate}: ModelInputChangeParams) => {
    const unvalidatedParams = {
      ...modelStateWithParameters.parameters,
      [key]: value,
    }

    const params = validate
      ? validateAndFilterParameters(modelInputSchema?.parameters || [], unvalidatedParams)
      : unvalidatedParams

    manager.setParameters(params)
    manager.setParametersHasChanges(true)
  }

  const handleClearHistory = useCallback(() => {
    modelClient.stopStreamingMessages(Panel.Main)
    manager.clearUserSystemPromptAndVariables()
    manager.resetHistory()
  }, [manager, modelClient])

  return (
    <div className="d-flex position-sticky top-0 p-2 border-bottom bgColor-muted flex-items-center">
      <Button
        size="small"
        className="mr-2"
        onClick={() => setShowVariablesDialog(true)}
        disabled={variableKeys.length === 0}
        {...testIdProps('edit-variables')}
      >
        Edit <span>{isMobile ? 'vars' : 'variables'}</span>
      </Button>
      <ParameterSettingsMenu model={model} handleModelParamsChange={handleModelParamsChange} />
      <div className="flex-1" />
      <IconButton
        icon={TrashIcon}
        size="small"
        aria-label="Clear prompts, variables, and chat"
        className="ml-2"
        disabled={!systemPrompt && !prompt}
        onClick={handleClearHistory}
        {...testIdProps('clear-prompt')}
      />
    </div>
  )
}

function PromptPaneContents({
  setVariableKeys,
  variableKeys,
}: {
  setVariableKeys: (keys: string[]) => void
  variableKeys: string[]
}) {
  const {messagePairs, prompt, systemPrompt} = usePromptState()
  const manager = usePromptManager()

  // New handlers for the PromptAutocompleteInput component
  const handleManagerPromptInput = useCallback(
    (promptInput: string) => {
      manager.setPromptInput(promptInput)
      setVariableKeys(referencedVariablesInState({prompt: promptInput, systemPrompt, messagePairs}))
    },
    [manager, messagePairs, setVariableKeys, systemPrompt],
  )

  const handleManagerSystemPrompt = useCallback(
    (newPrompt: string) => {
      manager.setSystemPrompt(newPrompt)
      setVariableKeys(referencedVariablesInState({systemPrompt: newPrompt, prompt, messagePairs}))
    },
    [manager, messagePairs, prompt, setVariableKeys],
  )

  const handleManagerMessagePairs = useCallback(
    (messagePairInput: MessagePair[]) => {
      manager.setMessagePairs(messagePairInput)
      setVariableKeys(referencedVariablesInState({messagePairs: messagePairInput, prompt, systemPrompt}))
    },
    [manager, prompt, setVariableKeys, systemPrompt],
  )

  return (
    <div className={styles.promptPane}>
      <form>
        <div className="mb-3" {...testIdProps('system-autocomplete-component')}>
          <PromptAutocompleteInput
            label="System"
            prompt={systemPrompt ?? ''}
            setPromptInput={handleManagerSystemPrompt}
            variableKeys={variableKeys}
            textareaPlaceholder="Define the model's behavior or role. Example: 'You are a spaceship captain telling intergalactic tales.'"
            showImprovePrompt
          />
        </div>

        <div className="mb-3" {...testIdProps('prompt-autocomplete-component')}>
          <PromptAutocompleteInput
            label="User"
            prompt={prompt}
            setPromptInput={handleManagerPromptInput}
            variableKeys={variableKeys}
            textareaPlaceholder="Enter the task or question for the model. Example: 'Write me a song about GitHub.' Use {{input}} for placeholders."
          />
        </div>

        <PromptMessagePair
          messagePairs={messagePairs ?? []}
          setMessagePairs={handleManagerMessagePairs}
          variableKeys={variableKeys}
        />
      </form>
    </div>
  )
}

const PromptMessagesContainer = ({
  ref: messagesContainerRef,
  model,
}: {
  model: ModelState
  ref?: React.RefObject<HTMLDivElement | null>
}) => {
  const {isLoading, messages} = model
  const lastIndex = messages.length - 1
  const {currentUser} = useUser()

  return (
    <div className="p-3 overflow-auto flex-1" ref={messagesContainerRef}>
      {messages.map((message, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <div key={`${message.role}-${index}`}>
          <PlaygroundChatMessageHeader
            message={message}
            model={model.catalogData}
            currentUser={currentUser}
            isLoading={index === lastIndex && isLoading}
          />
          <PlaygroundChatMessage
            model={model}
            message={message}
            index={index}
            isLoading={index === lastIndex && isLoading}
            isError={message.role === 'error'}
            lastIndex={index === lastIndex}
            handleClearHistory={() => {}}
          />
        </div>
      ))}
    </div>
  )
}
PromptMessagesContainer.displayName = 'PromptMessagesContainer'

function PromptBlankslate({
  modelClient,
  promptExtractionCodeSnippet,
  promptExtractionModel,
}: Pick<PromptProps, 'modelClient'> & {
  promptExtractionCodeSnippet: string | undefined
  promptExtractionModel: Model | undefined
}) {
  const {systemPrompt, model} = usePromptState()
  const manager = usePromptManager()
  const handleExtractedPrompt = useCallback(
    (extractedPrompt: string) => {
      manager.setPromptInput(extractedPrompt)
      manager.sendMessage(model, modelClient, systemPrompt, extractedPrompt)
    },
    [manager, modelClient, model, systemPrompt],
  )
  const {extractingPrompt} = useExtractedPrompt(
    promptExtractionModel || model.catalogData,
    modelClient,
    handleExtractedPrompt,
    promptExtractionCodeSnippet,
  )

  return (
    <div className={styles.blankSlate}>
      <Blankslate>
        <Blankslate.Visual>
          {extractingPrompt ? <Spinner size="medium" /> : <StackIcon size="medium" />}
        </Blankslate.Visual>
        <Blankslate.Heading>Iterate on your prompt</Blankslate.Heading>
        <Blankslate.Description>
          {extractingPrompt ? (
            'Extracting the prompt from your code...'
          ) : (
            <>
              Use the prompt editor to run a single prompt repeatedly, refining it and adjusting your{' '}
              <code>{'{{input}}'}</code> to achieve the perfect response.
            </>
          )}
        </Blankslate.Description>
      </Blankslate>
    </div>
  )
}
